import React, { useState, useCallback } from 'react';
import { transformIframes } from '../../../utils/utils';
import styles from './styles/ChoicesBody.module.css';
import HintModal from './HintModal';
import { useScenario } from '../../../context/ScenarioContext';
import { TUTORIAL_TARGETS, tutorialAttr } from '../../../tutorials';

const ChoicesBody = ({
    choices,
    scenario,
    setCurrentSituationId,
    isScenarioLogged,
    preview,
    userKeys = [],
    applyKeyChanges,
    assignmentId,
    universalLock,
    userLock,
}) => {
    const [showHintModal, setShowHintModal] = useState(false);
    const [hintText, setHintText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const { submitDecision } = useScenario();

    const handleClickChoice = useCallback(async (choice) => {
        if (isProcessing) return;
        setIsProcessing(true);

        try {
            // Where the keys are kept (device, session or account) is the
            // hook's business; see hooks/usePlayerKeys.js.
            if (applyKeyChanges) {
                await applyKeyChanges({
                    grants: choice.grantsKeys,
                    removes: choice.removesKeys,
                    source: { choiceId: choice._id },
                });
            }

            if (isScenarioLogged && !preview) {
                await submitDecision({
                    situationId: choice.situation,
                    choiceId: choice._id,
                    correctness: choice.correctness,
                    type: 'choice',
                    userInput: null,
                    nextSituationId: choice.nextSituation?._id,
                    assignmentId: assignmentId,
                    fromToC: false
                });
            }

            if (choice.nextSituation?._id && choice?.situation !== choice?.nextSituation?._id) {
                // The live teacher view is updated by ViewScenario's progress
                // effect when this position change lands — navigation itself
                // never waits on the socket.
                setCurrentSituationId(choice.nextSituation._id);
            } else {
                if (choice.hasHint && choice?.situation === choice?.nextSituation?._id) {
                    setHintText(choice.hint);
                    setShowHintModal(true);
                } else {
                    console.warn("Choice has no next situation:", choice);
                }
            }
            setIsProcessing(false);
        } catch (error) {
            console.error("Error processing choice:", error);
            setIsProcessing(false);
        }
    }, [
        isProcessing,
        applyKeyChanges,
        submitDecision,
        isScenarioLogged,
        preview,
        setCurrentSituationId,
        assignmentId,
    ]);

    const closeModal = useCallback(() => {
        setShowHintModal(false);
        setHintText('');
    }, []);

    const isChoiceVisible = useCallback((choice) => {
        if (!choice.key) {
            return true;
        }
        const hasKey = userKeys.some((id) => String(id) === String(choice.key));
        if (choice.showIfKey) {
            return hasKey;
        } else if (choice.hideIfKey) {
            return !hasKey;
        }
        return true;
    }, [userKeys]);

    const filteredChoices = choices.filter(isChoiceVisible);

    return (
        <>
            <div className={styles.container}>
                {filteredChoices.map((choice) => {
                    const isDisabled = isProcessing || universalLock || userLock;

                    return (
                        <div
                            key={choice._id}
                            role="button"
                            tabIndex={0}
                            className={`${styles.choice} ${isDisabled ? styles.disabledChoice : ''}`}
                            onClick={() => !isDisabled && handleClickChoice(choice)}
                            onKeyPress={(e) => {
                                if (!isDisabled && e.key === 'Enter') {
                                    handleClickChoice(choice);
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: transformIframes(choice.text) }}
                            aria-disabled={isDisabled}
                            {...tutorialAttr(TUTORIAL_TARGETS.PREVIEW_CHOICE, choice._id)}
                        />
                    );
                })}
            </div>

            {showHintModal && (
                <HintModal hintText={hintText} closeModal={closeModal} />
            )}
        </>
    );
};

export default ChoicesBody;
